import { Form, FormikProvider, useFormik } from 'formik';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useSWR from 'swr';

import APIMethodKeys from '../../../client/APIMethodKeys';
import { EventListFiltersProps } from '../../../typings/EventList.interface';
import { RequestListFiltersProps } from '../../../typings/RequestList.interface';
import { GlobalContext } from '../../contexts/GlobalContext';
import { UserContext } from '../../contexts/UserContext';
import { useDatePreferences } from '../../hooks/useDatePreferences';
import { DashboardContext } from '../../scenes/DashboardScene/DashboardContext';
import Button from '../base/Button';
import FilterChip from '../base/FilterChip';
import Dropdown from '../Dropdown';
import { Div } from '../helpers/StyledUtils';
import { FilterComponent, FilterFormValues } from './FilterComponents';
import { MoreFiltersMenu } from './MoreFiltersMenu';
import { OutpostAttemptFilters } from '../../scenes/OutpostScene/Attempts/utils';
import { OutpostEventFilters } from '../../scenes/OutpostScene/Events/EventsView';

export const StyledFilterButtonsWrapper = styled(Div)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: ${({ theme }) => theme.spacing(2)};
  overflow-x: auto;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  > * {
    flex-shrink: 0;
  }
`;

interface FiltersProps {
  route: string;
  filters:
    | EventListFiltersProps
    | RequestListFiltersProps
    | OutpostEventFilters
    | OutpostAttemptFilters;
  unstructured_component?: FilterComponent;
  components: FilterComponent[];
  onFilterChanged: (filters: object) => void;
}

export const FiltersContext = createContext<{
  filters:
    | EventListFiltersProps
    | RequestListFiltersProps
    | OutpostEventFilters
    | OutpostAttemptFilters;
  components: FilterComponent[];
  active_filters: FilterComponent[];
  filter_buttons: FilterComponent[];
  route: string;
  unstructured_component?: FilterComponent;
  values: FilterFormValues;
  formik: ReturnType<typeof useFormik<FilterFormValues>>;
  handleToggleActiveFilter: (key: string) => void;
  getDeleteButton: (filter: FilterComponent) => JSX.Element;
  active_filter_without_unstructured: FilterComponent[];
  removeFilter: (filter: FilterComponent) => void;
}>({
  filters: {},
  components: [],
  active_filters: [],
  filter_buttons: [],
  route: '',
  values: {} as FilterFormValues,
  formik: {} as ReturnType<typeof useFormik<FilterFormValues>>,
  handleToggleActiveFilter: () => {
    return;
  },
  getDeleteButton: () => {
    return <></>;
  },
  active_filter_without_unstructured: [],
  removeFilter: () => {
    return;
  },
});

const FiltersProvider: React.FC<FiltersProps & { children: React.ReactNode }> = ({
  filters,
  components,
  route,
  onFilterChanged,
  unstructured_component,
  children,
}) => {
  if (unstructured_component) {
    components = [...components, unstructured_component];
  }
  const { team } = useContext(DashboardContext);
  const [active_filters, setActiveFilters] = useState<FilterComponent[]>([]);
  const [save_error, setSaveError] = useState<string | undefined>(undefined);
  // Serialized URL-derived form values from the last run of the sync effect below.
  // Re-syncing only when this content changes (rather than comparing against live formik
  // values) keeps unsubmitted edits — e.g. JSON mid-typing in an open filter dropdown —
  // from being clobbered when a background list refresh re-renders the parent and passes
  // a same-content, new-identity `filters` object.
  const last_synced_values_ref = useRef<string | undefined>(undefined);

  useEffect(() => {
    const new_active_filters: FilterComponent[] = [...active_filters];

    const active_filters_by_key = active_filters.reduce(
      (object, f) => ({ ...object, [f.filter_key]: f }),
      {},
    );

    let active_filters_changed = false;
    const new_values = {};
    components.forEach((f) => {
      const formattedValue = f.formatForForm(filters[f.filter_key], filters);
      new_values[f.form_name] = formattedValue;
      const active = f.isActive(formattedValue);
      if (active && !active_filters_by_key[f.filter_key]) {
        new_active_filters.push(f);
        active_filters_by_key[f.filter_key] = f;
        active_filters_changed = true;
        return;
      } else if (!active) {
        delete active_filters_by_key[f.filter_key];
        const index = new_active_filters.findIndex((a) => a.filter_key === f.filter_key);
        if (index !== -1) {
          new_active_filters.splice(index, 1);
          active_filters_changed = true;
        }
      }
    });

    // Only write state when the URL-derived content actually changed. `filters` gets a
    // new identity on every parent render; unconditionally calling setValues here
    // dispatches SET_VALUES and kicks off a full validation pass (SET_ISVALIDATING
    // true/false + SET_ERRORS) each time, which can cascade into "Maximum update depth
    // exceeded" — and overwriting formik with URL state while the user has unsubmitted
    // edits wipes what they are typing.
    if (active_filters_changed) {
      setActiveFilters(new_active_filters);
    }
    const serialized_values = JSON.stringify(new_values);
    if (last_synced_values_ref.current !== serialized_values) {
      const is_first_sync = last_synced_values_ref.current === undefined;
      last_synced_values_ref.current = serialized_values;
      // On mount formik was initialized from this same `filters`, so just record the
      // baseline; dispatching setValues would only trigger a redundant validation pass.
      if (!is_first_sync) {
        setValues(new_values);
      }
    }
  }, [filters]);

  useEffect(() => {
    const new_active_filters = active_filters.filter(
      (f) => !!components.find((filter) => f.filter_key === filter.filter_key),
    );

    if (new_active_filters.length !== active_filters.length) {
      const new_values = { ...values };
      const removed_keys = Object.keys(new_values).filter(
        (key) => !new_active_filters.find((f) => f.filter_key === key),
      );
      removed_keys.forEach((key) => delete new_values[key]);
      setActiveFilters(new_active_filters);
      setValues(new_values);
    }
  }, [components]);

  const formik = useFormik<FilterFormValues>({
    initialValues: components.reduce((obj, f) => {
      obj[f.form_name] = f.formatForForm(filters[f.filter_key], filters);
      return obj;
    }, {}) as FilterFormValues,
    validate: (values) => {
      return active_filters
        .filter((f) => !!f.validate)
        .reduce((errors, filter) => {
          const filter_errors = filter.validate?.(values[filter.form_name]);
          if (filter_errors && Object.keys(filter_errors).length > 0) {
            errors[filter.form_name] = filter_errors;
          }
          return errors;
        }, {});
    },
    onSubmit: (values) => {
      let should_update = false;

      components.forEach((f) => {
        const formated_filter = f.formatForForm(filters[f.filter_key], filters);
        if (!f.isActive(values[f.form_name]) && !f.isActive(formated_filter)) {
          return;
        }
        if (JSON.stringify(values[f.form_name]) !== JSON.stringify(formated_filter)) {
          should_update = true;
        }
      });

      if (!should_update) {
        return;
      }

      const new_filters = components.reduce((query, { filter_key, form_name, formatForQuery }) => {
        const formatted_value = formatForQuery(values[form_name]);

        // Handle nested filter keys like 'request.body', 'request.headers'
        if (filter_key.includes('.')) {
          const [parent_key] = filter_key.split('.');
          if (formatted_value !== undefined) {
            if (!query[parent_key]) {
              query[parent_key] = {};
            }
            query[parent_key] = {
              ...(query[parent_key] as object),
              ...(formatted_value as object),
            };
          }
        } else {
          query[filter_key] = formatted_value;
        }

        return query;
      }, {});

      onFilterChanged(new_filters);

      if (save_error) {
        setSaveError(undefined);
      }
    },
  });

  const { handleSubmit, setValues, values } = formik;

  const filters_by_key: { [key: string]: FilterComponent } = (() => {
    return components.reduce((object, f) => ({ ...object, [f.filter_key]: f }), {});
  })();

  const handleToggleActiveFilter = (key: string) => {
    const filter = active_filters.find((a) => a.filter_key === key);
    if (filter) {
      const new_active_filters = [...active_filters];
      new_active_filters.splice(
        active_filters.findIndex((f) => f.filter_key === key),
        1,
      );
      setActiveFilters(new_active_filters);
      const new_filters_by_key = { ...filters_by_key };
      new_active_filters.forEach((f) => delete new_filters_by_key[f.filter_key]);
      handleSubmit();
      setValues((prev) => ({
        ...prev,
        [filter.form_name]: filter.formatForForm(null, {}),
      }));
    } else {
      setActiveFilters((prev) => [...prev, filters_by_key[key]]);
    }
  };

  const removeFilter = (filter: FilterComponent) => {
    handleToggleActiveFilter(filter.filter_key);
  };

  const getDeleteButton = (filter: FilterComponent) => {
    return <Button ghost large icon="close" onClick={() => removeFilter(filter)} m={{ l: 2 }} />;
  };

  const filter_buttons = components.filter(
    (f) => !f.hidden || active_filters.some((a) => a.filter_key === f.filter_key),
  );

  const active_filter_without_unstructured = active_filters.filter((a) =>
    unstructured_component ? a.filter_key !== unstructured_component?.filter_key : true,
  );

  const contextValue = {
    filters,
    components,
    active_filters,
    filter_buttons,
    route,
    unstructured_component,
    values,
    formik,
    handleToggleActiveFilter,
    getDeleteButton,
    active_filter_without_unstructured,
    removeFilter,
  };

  return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>;
};

const FilterButtons: React.FC = () => {
  const { filter_buttons, active_filters, filters, formik, removeFilter } =
    useContext(FiltersContext);
  const { HookdeckAPI } = useContext(GlobalContext);
  const { user } = useContext(UserContext);
  const { team } = useContext(DashboardContext);
  const { timezone } = useDatePreferences();

  const { unique_webhook_ids, unique_destination_ids, unique_source_ids, unique_tenant_ids } =
    (() => {
      const webhook_id = formik.values.webhook_id || [];
      const destination_id = formik.values.destination_id || [];
      const source_id = formik.values.source_id || [];
      const tenant_id = formik.values.tenant_id || [];

      const unique_webhook_ids = Array.from(new Set(webhook_id)).sort();
      const unique_destination_ids = Array.from(new Set(destination_id)).sort();
      const unique_source_ids = Array.from(new Set(source_id)).sort();
      const unique_tenant_ids = Array.from(new Set(tenant_id)).sort();

      return { unique_webhook_ids, unique_destination_ids, unique_source_ids, unique_tenant_ids };
    })();

  const { data: webhooks } = useSWR(
    unique_webhook_ids.length > 0 ? APIMethodKeys.webhooks.list({ id: unique_webhook_ids }) : null,
    () =>
      HookdeckAPI.webhooks.list(
        unique_webhook_ids.length > 0
          ? { id: unique_webhook_ids, limit: unique_webhook_ids.length }
          : { limit: 1 },
      ),
  );

  const { data: destinations } = useSWR(
    unique_destination_ids.length > 0
      ? APIMethodKeys.destinations.list({ id: unique_destination_ids })
      : null,
    () =>
      HookdeckAPI.destinations.list(
        unique_destination_ids.length > 0
          ? { id: unique_destination_ids, limit: unique_destination_ids.length }
          : { limit: 1 },
      ),
  );

  const { data: sources } = useSWR(
    unique_source_ids.length > 0 ? APIMethodKeys.sources.list({ id: unique_source_ids }) : null,
    () =>
      HookdeckAPI.sources.list(
        unique_source_ids.length > 0
          ? { id: unique_source_ids, limit: unique_source_ids.length }
          : { limit: 1 },
      ),
  );

  const { data: tenants } = useSWR(
    unique_tenant_ids.length > 0
      ? APIMethodKeys.outpost.tenants.list({ id: unique_tenant_ids })
      : null,
    () =>
      HookdeckAPI.outpost.tenants.list(
        unique_tenant_ids.length > 0
          ? { id: unique_tenant_ids, limit: unique_tenant_ids.length }
          : { limit: 1 },
      ),
  );

  const { data: team_members } = useSWR(APIMethodKeys.teams.listMembers(team!.id), () =>
    HookdeckAPI.teams.listMembers(),
  );

  const webhooks_by_id = webhooks
    ? webhooks.models.reduce((object, webhook) => ({ ...object, [webhook.id]: webhook }), {})
    : {};

  const destinations_by_id = destinations
    ? destinations.models.reduce(
        (object, destination) => ({ ...object, [destination.id]: destination }),
        {},
      )
    : {};

  const sources_by_id = sources
    ? sources.models.reduce((object, source) => ({ ...object, [source.id]: source }), {})
    : {};

  const tenants_by_id = tenants
    ? tenants.models.reduce((object, tenant) => ({ ...object, [tenant.id]: tenant }), {})
    : {};

  const team_members_by_id = team_members
    ? team_members.reduce(
        (object, team_member) => ({ ...object, [team_member.user_id]: team_member }),
        {},
      )
    : {};

  const data_by_id = {
    webhook_id: webhooks_by_id,
    destination_id: destinations_by_id,
    source_id: sources_by_id,
    team_members: team_members_by_id,
    tenant_id: tenants_by_id,
  };

  const { primary_filters, secondary_filters } = filter_buttons.reduce(
    (filters, f) => {
      if (f.priority === 'primary' || active_filters.some((a) => a.filter_key === f.filter_key)) {
        filters.primary_filters.push(f);
      } else if (f.priority === 'secondary') {
        filters.secondary_filters.push(f);
      }
      return filters;
    },
    { primary_filters: [], secondary_filters: [] } as {
      primary_filters: FilterComponent[];
      secondary_filters: FilterComponent[];
    },
  );

  return (
    <StyledFilterButtonsWrapper>
      {primary_filters.map((f) => {
        const filter = active_filters.find((a) => a.filter_key === f.filter_key);
        const is_active = !!filter;

        const values = formik.values[f.form_name];

        return (
          <Dropdown
            key={f.filter_key}
            portal
            renderToggle={(opened, handleToggle) => (
              <FilterChip
                key={f.filter_key}
                label={f.label}
                label_icon={f.icon}
                value={filter?.valuesToString(values, data_by_id, user || undefined, timezone)}
                m={{ y: 1 }}
                dashed={!is_active}
                icon={is_active ? 'close' : 'add'}
                active={opened}
                hide_chevron={!f.Component}
                onClick={() => {
                  if (!f.Component) return null;

                  handleToggle(true);
                }}
                onIconClick={is_active ? () => removeFilter(f) : undefined}
              />
            )}
            onToggle={(opened) => {
              if (!opened) {
                formik.handleSubmit();
              }
            }}
            parent_width={{ min: f.width || 300, max: f.width || 300 }}>
            {(toggle) =>
              f.Component && (
                <f.Component
                  key={f.filter_key}
                  label={f.label}
                  name={f.form_name}
                  filters={filters}
                  {...(f.component_props as any)}
                  onSubmit={() => {
                    formik.handleSubmit();
                    toggle(false);
                  }}
                  onReset={formik.resetForm}
                />
              )
            }
          </Dropdown>
        );
      })}
      {secondary_filters.length > 0 && (
        <MoreFiltersMenu
          filters={secondary_filters}
          onToggle={(opened) => {
            if (!opened) {
              formik.handleSubmit();
            }
          }}
        />
      )}
    </StyledFilterButtonsWrapper>
  );
};

const FilterForm: React.FC = () => {
  const { formik } = useContext(FiltersContext);

  return (
    <FormikProvider value={formik}>
      <Form id="filters-form">
        <FilterButtons />
      </Form>
    </FormikProvider>
  );
};

// Create the main Filters component and add subcomponents
const Filters = (props: FiltersProps) => {
  return (
    <FiltersProvider {...props}>
      <Div w={100}>
        <FilterForm />
      </Div>
    </FiltersProvider>
  );
};
Filters.Form = FilterForm;
Filters.Provider = FiltersProvider;

export default Filters;
